import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Input } from "../components/ui/input";

const MAX_DAYS = 120;

// Paramètres › Revenus — the payment delay of an installment (Ordre 17, D-34: 15 days by default). settings:view to read,
// settings:manage to change; each installment keeps the delay of its creation, so a change never moves an existing deadline.
export default function SettingsBilling() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => { setSaved(settings); setValue(String(settings.payment_delay_days)); };
  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/billing");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const n = Number(value);
  const invalid = !Number.isInteger(n) || n < 0 || n > MAX_DAYS || value.trim() === "";
  const dirty = saved != null && String(saved.payment_delay_days) !== value;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/billing", { payment_delay_days: n });
      apply(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-billing">
      <SectionHeader title="Revenus" description="Le délai laissé aux pharmacies pour payer chaque échéance de leur abonnement." />
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <label htmlFor="billing-delay" className="text-sm font-medium text-slate-900">Délai de paiement</label>
        <div className="flex items-center gap-2">
          <Input id="billing-delay" type="number" min={0} max={MAX_DAYS} value={value} disabled={!canManage} className="w-24"
            onChange={(e) => setValue(e.target.value)} aria-invalid={invalid} data-testid="billing-delay" />
          <span className="text-sm text-slate-500">jours après le début de la période facturée</span>
        </div>
        {invalid && <p className="text-xs text-red-600">Entre 0 et {MAX_DAYS} jours.</p>}
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4 border-t border-slate-100 pt-3">
          <li>Une échéance non payée à cette date apparaît dans « Impayés », avec son ancienneté. Rien n'est envoyé à la pharmacie et rien n'est suspendu automatiquement.</li>
          <li>Un changement ne vaut que pour les échéances créées ensuite : chaque échéance garde le délai de sa création.</li>
        </ul>
      </div>
      <SaveBar dirty={dirty} saveDisabled={invalid} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
